import { clsx } from 'clsx'
import { View } from 'react-native'

import { Typography } from '@/src/elements/text/Typography'
import { LinkifyText } from '@/src/general/components/LinkifyText'

import type { TChatMessageProps } from '../ChatMessage.types'
import { ChatMessageBubble } from '../ChatMessageBubble'
import { ChatMessageHeader, type TChatMessageHeaderProps } from '../ChatMessageHeader'
import { ChatMessageTextWrapper } from '../ChatMessageTextWrapper'
import { ChatMessageThumbnail, type TChatMessageThumbnailProps } from '../ChatMessageThumbnail'
import { ChatMessageTimestamp } from '../ChatMessageTimestamp'

type TChatMessageReplyProps = Pick<
  TChatMessageProps,
  'createdAt' | 'firstOfBatch' | 'isSender' | 'lastOfBatch' | 'onDeleteMessage' | 'onReportMessage' | 'showWarning'
> & {
  text: string
  repliedMessage: {
    thumbnail?: Omit<TChatMessageThumbnailProps, 'color'>
    header?: Omit<TChatMessageHeaderProps, 'color'>
    text: string
  }
}

export const ChatMessageReply = ({
  createdAt,
  firstOfBatch,
  isSender,
  lastOfBatch,
  repliedMessage,
  onDeleteMessage,
  onReportMessage,
  showWarning,
  text,
}: TChatMessageReplyProps) => (
  <ChatMessageTextWrapper
    createdAt={createdAt}
    isSender={isSender}
    lastOfBatch={lastOfBatch}
    onDeleteMessage={onDeleteMessage}
    onReportMessage={onReportMessage}
    showWarning={showWarning}
  >
    {({ mainLastOfBatch }) => (
      <ChatMessageBubble
        lastOfBatch={mainLastOfBatch}
        firstOfBatch={firstOfBatch}
        isSender={isSender}
        className={isSender ? 'bg-white' : 'bg-secondary-bg-medium'}
      >
        <View className="p-2 gap-2">
          {repliedMessage.thumbnail && (
            <ChatMessageThumbnail {...repliedMessage.thumbnail} color={isSender ? 'secondary' : 'default'} />
          )}
          <View className="px-2 gap-2">
            {repliedMessage.header && (
              <ChatMessageHeader {...repliedMessage.header} color={isSender ? 'default' : 'secondary'} />
            )}
            {repliedMessage.text && (
              <Typography
                contrast={isSender ? 'high' : 'default'}
                colors={isSender ? 'default' : 'secondary'}
                size="xs"
              >
                <LinkifyText text={repliedMessage.text} />
              </Typography>
            )}
          </View>
        </View>
        <ChatMessageBubble
          lastOfBatch={mainLastOfBatch}
          firstOfBatch={firstOfBatch}
          isSender={isSender}
          className={clsx('px-4 py-2 shadow-none self-stretch', isSender ? 'bg-secondary-bg-inverse' : 'bg-white')}
        >
          <Typography contrast={isSender ? 'high' : 'default'} colors={isSender ? 'white' : 'default'}>
            <LinkifyText text={text} />
          </Typography>
          {mainLastOfBatch && <ChatMessageTimestamp color={isSender ? 'white' : 'default'} createdAt={createdAt} />}
        </ChatMessageBubble>
      </ChatMessageBubble>
    )}
  </ChatMessageTextWrapper>
)
